// Detalle de un pre-lanzamiento: los productos, su ficha y sus fotos, más el
// botón de publicar — ADR-2026-09-06-lanzamientos-modelo-de-datos.
//
// Desktop: lista de productos a la izquierda, ficha del elegido a la derecha, como
// la pantalla de productos de una tienda. Mobile: la lista, y la ficha entra a
// pantalla completa.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalConfirmar } from '../../components/ModalConfirmar';
import { useSidebar } from '../../context/SidebarContext';
import { useVolverAtras } from '../../hooks/useVolverAtras';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  Loader2,
  Check,
  AlertCircle,
  Upload,
  Download,
  ChevronRight,
  ImagePlus,
  Search,
  X,
  FolderTree,
  Trash2,
  Images,
  ArrowRight,
} from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import FichaDeProducto from './FichaDeProducto';
import { PanelDeCategorias } from './Categorias';
import { useCategorias } from './useCategorias';
import PanelDeIa, { type ProductoParaIa } from './PanelDeIa';
import PanelDeComentarios from './Comentarios';
import { useComentarios } from './useComentarios';
import { BarraDeSeleccion, SheetDeCategoria } from './AccionesEnLote';
import { pasosDelLanzamiento, pasosDelLanzamientoVacio, useRecorrido } from './recorridos';
import {
  camposParaCanal,
  completarFichaConIa,
  type CampoIa,
  type SugerenciaDeFicha,
} from './fichaIa';
import { CalendarioDeRedes } from './redes/CalendarioDeRedes';
import { sePuedeElegirQuePasaALaHora } from './redes/avisos';
import { ModalConectarInstagram, ToggleDeModo } from './redes/ToggleDeModo';
import {
  fetchCuentaDeInstagram,
  fetchModoPublicacion,
  setModoPublicacion,
  type ModoDePublicacion,
} from './redes/postsApi';
import { aplicarSugerencia } from './aplicarSugerencia';
import { caminoDe } from './arbolDeCategorias';
import TiendaNubeConnectModal from '../../components/TiendaNubeConnectModal';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import {
  abrirVentanaOAuth,
  connectTnEnVentana,
  fireTnExport,
  getTnStatus,
  leerPublicadosEnTn,
} from '../../lib/tiendaNube';
import { cambiosContraLaTienda, type ProductoEnLaTienda } from './cambiosContraLaTienda';
import type { LecturaDeLaTienda } from './EnTuTienda';
import { traerCatalogoAlConectar } from '../../lib/traerCatalogoAlConectar';
import { canalDe, creaProductos, DESTINOS_QUE_CREAN, nombreCanal, nombresDeDestinos, reglasDeDestinos, type CanalLanzamiento } from './canales';
import { MercadoLibreIcon } from '../../components/MercadoLibreIcon';
import { TiendaNubeIconCompacto } from '../../components/TiendaNubeIcon';
import { publicarEnMl } from '../../lib/mercadoLibrePublicar';
import { useMercadoLibreDisponible } from '../../hooks/useMercadoLibreDisponible';
import { frenoDeMl, publicarLanzamiento, publicarLanzamientoEnMl, YA_PUBLICANDO, yaPublicadoEnMl } from './publicarLanzamiento';
import {
  SIN_DATO,
  resumenDeCarpetas,
  resumenDeFila,
  type DetalleDeVariante,
} from './resumenDeFila';
import { nombreDelZip, planDeExport } from './exportarZip';
import type { CategoriaDeTienda } from './lanzamientosApi';
import { descargarComoZip, type ArchivoDeLote } from '../../utils/descargarLote';
import { SelectorDeCreaciones } from './SelectorDeCreaciones';
import { SelectorDeProducto } from '../../components/EnviarALanzamiento';
import { avisoDeSuma, itemsParaProducto, repartirPorProducto } from './sumarCreaciones';
import { useOpcionesDeDescarga } from '../../components/descarga/useOpcionesDeDescarga';
import { filtrarProductos } from './buscarProductos';
import PopoverAnclado from './PopoverAnclado';
import { boton } from './botones';
import { PanelDeLaTienda, type ConexionDeTienda } from './PanelDeLaTienda';
import { BarraDeLaFicha } from './BarraDeLaFicha';
import { contarEstados, estadoDeFila, type EstadoDeFila, type TonoDeEstado } from './estadoDeFila';
import {
  actualizarDestinos,
  borrarLanzamiento,
  contarComentariosPendientes,
  enviarImagenesALanzamiento,
  conDatosMl,
  fetchLanzamiento,
  productoPorVariante,
  firmarFull,
  marcarPublicacionIncompleta,
  sigueEnElBackend,
  marcarCategoriaDeProducto,
  problemasParaPublicar,
  quitarImagen,
  quitarProducto,
  reordenarImagenes,
  revisarPublicacion,
  type GeneradaParaElegir,
  type ItemAEnviar,
  type LanzamientoDetalle as Detalle,
  type ProductoDeLanzamiento,
} from './lanzamientosApi';
import { escucharCambiosDeLanzamiento } from './cambioDeLanzamiento';
import { lecturasEnOrden } from './lecturasEnOrden';
import { limiteDeEsperaMs } from './esperaDePublicacion';
import { copyDeErrorDePermiso, exigirPermisoDeTienda } from '../../lib/permisosDelEquipo';

/** Cada cuánto y hasta cuándo se pregunta si la publicación terminó. */
const POLL_MS = 2500;

/** El aviso de arriba de la tabla. `error` lo pinta de rojo. */
type Aviso = { texto: string; error: boolean };
const info = (texto: string): Aviso => ({ texto, error: false });
const falla = (texto: string): Aviso => ({ texto, error: true });

export default function LanzamientoDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, currentBrandId } = useBrandProfile();
  // El rubro de la MARCA activa: decide qué atributos de variante se sugieren.
  const { rubro } = useRubroMarcaActiva();
  const companyId = profile?.id_company ?? null;

  const [detalle, setDetalle] = useState<Detalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [elegido, setElegido] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [progreso, setProgreso] = useState<{ subidas: number; total: number } | null>(null);
  // Un aviso que cuenta algo que salió mal —falta un dato, la publicación no se
  // confirmó— va en rojo; el que cuenta algo que salió bien, en gris.
  const [aviso, setAviso] = useState<Aviso | null>(null);
  /**
   * El aviso habla del momento en que salió ("2 productos sin completar…"). Si la persona
   * cambia de pantalla (abre, cierra o cambia de ficha) o actualiza algo, ya puede no ser
   * cierto: se va. Si sigue faltando algo, el próximo "Publicar" lo vuelve a decir.
   */
  const despejarAviso = useCallback(() => setAviso(null), []);
  // ── Sumar creaciones desde la ficha ───────────────────────────────────────
  // (ADR-2026-09-17-la-ficha-vacia-del-lanzamiento-elige-creaciones)
  const [eligiendoCreaciones, setEligiendoCreaciones] = useState(false);
  /** Las elegidas que no salieron de ningún producto: esperan a que la persona elija uno. */
  const [sinProducto, setSinProducto] = useState<GeneradaParaElegir[]>([]);
  const [pedirConexion, setPedirConexion] = useState(false);
  const [conectando, setConectando] = useState(false);
  const [errorConexion, setErrorConexion] = useState<string | null>(null);
  const [pedirBorrado, setPedirBorrado] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [verCategorias, setVerCategorias] = useState(false);
  const [consulta, setConsulta] = useState('');

  // ── Selección múltiple ────────────────────────────────────────────────────
  // Lo tildado es lo que se publica, y arranca TODO tildado: la persona saca lo que
  // no quiere en vez de tener que marcar uno por uno lo que sí. Por eso se guarda lo
  // DESTILDADO y no lo tildado: un producto que se suma después entra tildado solo.
  // Las acciones en lote (IA, categoría, sacar) siguen actuando sobre lo tildado.
  const [excluidos, setExcluidos] = useState<Set<string>>(new Set());
  // Categoría y Sacar guardan A QUIÉNES van al abrirse, no un booleano: con el
  // diálogo abierto la búsqueda de atrás se puede cambiar (el foco no queda
  // atrapado), y eso no puede cambiar a qué productos se aplica lo que se confirma
  // (review Greptile #155). `null` = cerrado.
  const [categoriaEnLote, setCategoriaEnLote] = useState<string[] | null>(null);
  const [enLote, setEnLote] = useState(false);
  const [errorLote, setErrorLote] = useState<string | null>(null);
  const [borradoEnLote, setBorradoEnLote] = useState<string[] | null>(null);

  // ── Completar con IA (uno o varios: es el mismo camino) ───────────────────
  const [idsParaIa, setIdsParaIa] = useState<string[] | null>(null);
  const [pensando, setPensando] = useState(false);
  const [progresoIa, setProgresoIa] = useState<{ hechos: number; total: number } | null>(null);
  const [errorIa, setErrorIa] = useState<string | null>(null);
  const [sugerencias, setSugerencias] = useState<Map<string, SugerenciaDeFicha>>(new Map());

  // ── Comentarios ───────────────────────────────────────────────────────────
  const [verComentarios, setVerComentarios] = useState(false);
  /** Pendientes por producto. Es un número por fila: se pide con la pantalla,
   *  a diferencia de los hilos, que se traen recién al abrir el panel. */
  const [pendientes, setPendientes] = useState<Map<string, number>>(new Map());
  const pollRef = useRef<number | null>(null);

  // El árbol se carga UNA vez por pantalla y lo comparten el panel y la ficha de
  // cada producto: con una copia en cada uno, crear una categoría desde el panel
  // no aparecería en el selector hasta recargar.
  /**
   * La marca del LANZAMIENTO, no la activa.
   *
   * Publicar usaba `currentBrandId` y eso es un bug con dientes: en Tiendanube
   * cada marca tiene su propia tienda, así que con dos marcas y la otra activa,
   * el lanzamiento de una se subía a la tienda de la otra — sin error, sin
   * aviso, productos creados en la tienda equivocada.
   *
   * `brand_id` es nullable en `lanzamientos` (los creados antes de tener marca),
   * y ahí la activa es la única respuesta posible.
   */
  const brandDelLanzamiento = detalle?.brandId ?? currentBrandId;
  /**
   * Qué exige y qué entrega el destino de ESTE lanzamiento. Mientras el detalle
   * no cargó se asume Tiendanube, que es el destino histórico: es sólo para el
   * primer pintado, donde todavía no hay ni tabla ni botón que dependan de esto.
   */
  // Con varias tiendas, gana la regla más exigente de cada una
  // (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
  const destinos = useMemo(() => detalle?.destinos ?? ['tiendanube' as const], [detalle?.destinos]);
  const reglasDelCanal = useMemo(() => reglasDeDestinos(destinos), [destinos]);
  /** Un lanzamiento a tiendas que crean productos solas: se le pueden sumar o sacar tiendas. */
  const esDeTiendas = destinos.every(creaProductos);
  const ml = useMercadoLibreDisponible(detalle?.brandId ?? null, esDeTiendas);
  /** Las tiendas de este lanzamiento donde todavía no se puede publicar (Mercado Libre, hasta la fase 6). */
  const sinPublicar = destinos.filter((d) => !canalDe(d).sePuedePublicar);
  const exportaCarpetas = reglasDelCanal.salida === 'zip';
  /**
   * La conexión de Tienda Nube, para el panel de arriba
   * (ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta). `null` mientras se
   * pregunta; `undefined` si la consulta falló: el panel no dice nada antes que
   * afirmar algo que no sabe. Se vuelve a preguntar al cerrar el modal de conectar.
   */
  const [conexionTn, setConexionTn] = useState<ConexionDeTienda | undefined>(null);
  const vaATiendanube = destinos.includes('tiendanube');
  const hayDetalle = !!detalle;
  useEffect(() => {
    if (!hayDetalle || !vaATiendanube || pedirConexion) return;
    let vivo = true;
    void getTnStatus(brandDelLanzamiento).then((s) => {
      if (vivo) setConexionTn(s.error ? undefined : { conectada: s.connected, nombre: s.storeName });
    });
    return () => {
      vivo = false;
    };
  }, [hayDetalle, vaATiendanube, brandDelLanzamiento, pedirConexion]);

  /**
   * Lo que hoy está publicado de los productos que ya están en Tiendanube: cada ficha se edita contra
   * eso y la tabla dice cuántos cambios tiene cada uno
   * (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento). Se vuelve a leer al terminar
   * de publicar y con "Reintentar".
   */
  const [lecturaTienda, setLecturaTienda] = useState<
    | { estado: 'cargando' }
    | { estado: 'error' }
    | { estado: 'listo'; publicados: Map<number, ProductoEnLaTienda>; leidos: Set<number> }
    | null
  >(null);
  const [vueltaDeLectura, setVueltaDeLectura] = useState(0);
  const idsEnLaTienda = useMemo(
    () =>
      vaATiendanube && !exportaCarpetas
        ? [
            ...new Set(
              (detalle?.productos_ ?? []).flatMap((p) => (p.productoId && p.tnProductId != null ? [p.tnProductId] : [])),
            ),
          ].sort((a, b) => a - b)
        : [],
    [detalle?.productos_, vaATiendanube, exportaCarpetas],
  );
  const firmaDeIdsEnLaTienda = idsEnLaTienda.join(',');
  useEffect(() => {
    if (idsEnLaTienda.length === 0) {
      setLecturaTienda(null);
      return;
    }
    let vigente = true;
    // Lo que ya se leyó se sigue mostrando mientras se relee (no parpadea a "cargando").
    setLecturaTienda((prev) => (prev?.estado === 'listo' ? prev : { estado: 'cargando' }));
    void leerPublicadosEnTn(brandDelLanzamiento, idsEnLaTienda).then((m) => {
      if (!vigente) return;
      setLecturaTienda(m ? { estado: 'listo', publicados: m, leidos: new Set(idsEnLaTienda) } : { estado: 'error' });
    });
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaDeIdsEnLaTienda, brandDelLanzamiento, vueltaDeLectura]);

  /** Lo de la tienda para la ficha de un producto. `undefined` = no está en la tienda (se crea). */
  const lecturaDe = useCallback(
    (p: ProductoDeLanzamiento): LecturaDeLaTienda | undefined => {
      if (!lecturaTienda || !p.productoId || p.tnProductId == null) return undefined;
      if (lecturaTienda.estado !== 'listo') return lecturaTienda;
      // Uno recién sumado todavía no se leyó: "cargando", no "ya no está".
      if (!lecturaTienda.leidos.has(p.tnProductId)) return { estado: 'cargando' };
      const publicado = lecturaTienda.publicados.get(p.tnProductId);
      return publicado ? { estado: 'listo', publicado } : { estado: 'no-esta' };
    },
    [lecturaTienda],
  );

  const conexiones = useMemo(
    () => ({
      tiendanube: conexionTn,
      mercadolibre: ml.cargado ? { conectada: ml.conectada, nombre: null } : null,
    }),
    [conexionTn, ml.cargado, ml.conectada],
  );
  /**
   * Redes no muestra la tabla de productos sino un calendario: acá no hay fichas
   * que completar ni un momento de "publicar el lanzamiento", porque cada post
   * sale a su hora (ADR-2026-09-15-un-lanzamiento-de-redes-es-un-calendario-de-posts).
   */
  const esCalendario = reglasDelCanal.salida === 'redes';
  // El calendario ahora se abre desde su sección, `/instagram`: un link viejo a la ficha
  // (una notificación, el historial) termina ahí y no en una pantalla de la tienda
  // (ADR-2026-10-06-instagram-tiene-su-propia-seccion).
  useEffect(() => {
    if (detalle && esCalendario) navigate('/instagram', { replace: true });
  }, [detalle, esCalendario, navigate]);
  // Sólo Mercado Libre: "ya en la tienda" / "se va a crear" son de Tiendanube y no aplican (prueba del 5/10).
  const modoDeTabla: ModoDeTabla = exportaCarpetas
    ? 'carpetas'
    : destinos.every((d) => d === 'mercadolibre')
      ? 'mercadolibre'
      : 'tienda';

  /**
   * Qué hacer cuando llega la hora de un post. Vive acá y no en el calendario
   * porque es del LANZAMIENTO —su fila lo guarda— y su control está en el
   * encabezado, al lado de Borrar
   * (ADR-2026-09-15-el-modo-de-publicacion-va-en-el-encabezado).
   */
  const [modoRedes, setModoRedes] = useState<ModoDePublicacion>('avisar');
  const [cuentaIg, setCuentaIg] = useState<string | null>(null);
  const [pedirInstagram, setPedirInstagram] = useState(false);

  useEffect(() => {
    // Sin el interruptor a la vista no hay para qué pedirlos: serían dos
    // consultas por lanzamiento abierto para pintar algo que no se dibuja.
    if (!esCalendario || !sePuedeElegirQuePasaALaHora() || !id || !companyId) return;
    void fetchModoPublicacion(id).then(setModoRedes);
    void fetchCuentaDeInstagram(companyId).then(setCuentaIg);
  }, [esCalendario, id, companyId]);

  /**
   * Sin cuenta conectada no se pasa a automático: el lanzamiento quedaría
   * diciendo que publica solo cuando no tiene con qué. El modal lo explica y
   * ofrece conectarla.
   */
  const cambiarModoRedes = (nuevo: ModoDePublicacion) => {
    if (nuevo === 'automatico' && !cuentaIg) return setPedirInstagram(true);
    if (!id) return;
    setModoRedes(nuevo);
    void setModoPublicacion(id, nuevo);
  };

  const cat = useCategorias(companyId, brandDelLanzamiento);

  /** Cuántos cambios contra la tienda tiene cada producto (la etiqueta de la fila). */
  const cambiosPorProducto = useMemo(() => {
    const out = new Map<string, number>();
    for (const p of detalle?.productos_ ?? []) {
      const l = lecturaDe(p);
      if (l?.estado === 'listo') out.set(p.id, cambiosContraLaTienda(p, l.publicado, cat.categorias).cambios.length);
    }
    return out;
  }, [detalle?.productos_, lecturaDe, cat.categorias]);
  // El hilo del producto abierto, y solo mientras el panel está abierto: pasarle
  // `null` cuando está cerrado es lo que evita una consulta por cada ficha que se
  // abre. Lo que la tabla necesita —cuántos pendientes tiene cada uno— ya viaja
  // con el lanzamiento y es un número, no los textos.
  const com = useComentarios(verComentarios ? elegido : null, companyId);

  /**
   * Conectar la tienda desde el modal. La ventana del OAuth se abre SINCRÓNICAMENTE
   * dentro del click, antes de cualquier await: si se espera a la URL, Safari la
   * bloquea por pérdida del gesto — ADR-2026-08-07-tiendanube-popup-safari.
   */
  const conectarTienda = useCallback(() => {
    if (conectando) return;
    const popup = abrirVentanaOAuth();
    setConectando(true);
    setErrorConexion(null);
    void (async () => {
      try {
        await connectTnEnVentana(brandDelLanzamiento, popup);
        // Conectar trae el catálogo (ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo).
        void traerCatalogoAlConectar(brandDelLanzamiento);
        setPedirConexion(false);
        // Se retoma la intención original: conectó para publicar, así que publica.
        setAviso(info('Tienda conectada. Tocá Publicar para subir el lanzamiento.'));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'No se pudo conectar.';
        setErrorConexion(msg === 'Conexión cancelada.' ? null : msg);
      } finally {
        setConectando(false);
      }
    })();
  }, [conectando, brandDelLanzamiento]);

  // Las lecturas se pisan (el chat, el seguimiento de "publicando"): ver `lecturasEnOrden`.
  const lecturasRef = useRef(lecturasEnOrden());
  const cargar = useCallback(async () => {
    if (!id || !companyId) return;
    const esta = lecturasRef.current.empezar();
    const d = await fetchLanzamiento(id, companyId);
    if (!lecturasRef.current.mostrar(esta)) return;
    setDetalle(d);
    setCargando(false);
    // Nadie elegido al entrar: la pantalla abre en la tabla, que es la vista que
    // deja comparar los productos (ADR-2026-09-07-lanzamientos-tabla-de-productos).
    // Solo se limpia lo elegido si ese producto ya no está.
    setElegido((prev) => (prev && d?.productos_.some((x) => x.id === prev) ? prev : null));
    const pendientes = await contarComentariosPendientes((d?.productos_ ?? []).map((p) => p.id));
    if (lecturasRef.current.esLaMostrada(esta)) setPendientes(pendientes);
  }, [id, companyId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // El chat le sumó productos o lo mandó a publicar: se vuelve a leer sin recargar la página.
  useEffect(() => {
    if (!id) return;
    return escucharCambiosDeLanzamiento(id, () => void cargar());
  }, [id, cargar]);

  // Sumar o sacar una tienda del lanzamiento (ADR-2026-09-30-lanzamiento-a-varias-tiendas). Siempre
  // queda al menos una, y el orden es el de DESTINOS_QUE_CREAN (la primera pasa a ser el `canal`).
  const [cambiandoDestinos, setCambiandoDestinos] = useState(false);
  const cambiarDestinos = useCallback(
    async (nuevos: CanalLanzamiento[]) => {
      if (!detalle || nuevos.length === 0) return;
      const ordenados = DESTINOS_QUE_CREAN.filter((d) => nuevos.includes(d));
      setCambiandoDestinos(true);
      const ok = await actualizarDestinos(detalle.id, ordenados);
      setCambiandoDestinos(false);
      if (!ok) setAviso(falla('No se pudieron cambiar las tiendas del lanzamiento. Probá de nuevo.'));
      await cargar();
    },
    [detalle, cargar],
  );

  useEffect(
    () => () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    },
    [],
  );

  // Abierto mientras se publica desde OTRO lado (el chat, otra pestaña): el worker anota cómo
  // termina cada producto (ADR-2026-09-30-publicar-lanzamiento-a-tiendanube-bugs), así que acá
  // sólo se vuelve a leer hasta que no quede nada publicando. Antes quedaba "Publicando" para
  // siempre, porque el único que seguía el progreso era el botón de esta pantalla.
  const publicandoAfuera = detalle?.estado === 'publicando' && !publicando;
  const enVuelo = detalle?.productos_.filter((p) => p.estado === 'publicando').length ?? 0;
  useEffect(() => {
    if (!publicandoAfuera || !detalle?.id) return;
    const lanzamientoId = detalle.id;
    let desde = Date.now();
    const limite = limiteDeEsperaMs({ productos: enVuelo });
    let preguntando = false;
    const t = window.setInterval(async () => {
      if (Date.now() - desde > limite) {
        // Mientras el backend siga publicando, se sigue mirando: si no, la pantalla dejaba de mostrar qué
        // productos terminaron (review Greptile #160).
        if (preguntando) return;
        preguntando = true;
        const sigue = await sigueEnElBackend(lanzamientoId).catch(() => false);
        preguntando = false;
        if (!sigue) {
          window.clearInterval(t);
          return;
        }
        desde = Date.now();
      }
      void cargar();
    }, POLL_MS);
    return () => window.clearInterval(t);
    // `enVuelo` no está en las dependencias a propósito: cambia mientras termina, y reiniciaría el reloj.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicandoAfuera, cargar, detalle?.id]);

  const producto = detalle?.productos_.find((p) => p.id === elegido) ?? null;
  /**
   * Al abrir una ficha o pasar a otra con las flechas, la pantalla vuelve al principio:
   * si no, el producto nuevo aparecía scrolleado donde había quedado el anterior
   * (ADR-2026-10-06-la-ficha-ocupa-la-pantalla). Al principio de la PÁGINA y no de la
   * ficha, para que el título y "Publicar" sigan a la vista. Lo que scrollea es el panel
   * de contenido del cascarón, no la ventana: se busca el primer ancestro que scrollea.
   */
  const inicioDeLaFicha = useRef<HTMLDivElement>(null);
  // Abrir, cerrar o cambiar de ficha es cambiar de pantalla: el aviso de la anterior se va.
  useEffect(() => {
    despejarAviso();
  }, [elegido, despejarAviso]);
  useEffect(() => {
    if (!elegido) return;
    let el: HTMLElement | null = inicioDeLaFicha.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTo({ top: 0 });
    else window.scrollTo({ top: 0 });
  }, [elegido]);

  // El buscador filtra la TABLA, no el lanzamiento: `producto` sale de la lista
  // completa, así que la ficha abierta no depende de lo que se esté buscando.
  const visibles = useMemo(
    () => filtrarProductos(detalle?.productos_ ?? [], consulta, cat.categorias, elegido),
    [detalle?.productos_, consulta, cat.categorias, elegido],
  );

  const porId = useMemo(
    () => new Map((detalle?.productos_ ?? []).map((p) => [p.id, p])),
    [detalle?.productos_],
  );

  const elegidos = useMemo(
    () => new Set((detalle?.productos_ ?? []).filter((p) => !excluidos.has(p.id)).map((p) => p.id)),
    [detalle?.productos_, excluidos],
  );

  // Las acciones en lote (IA, categoría, sacar) van a lo tildado QUE SE VE. Con todo
  // tildado de entrada, buscar un producto y tocar "Sacar" se llevaba también los
  // tildados que la búsqueda escondía (review Greptile #155). Es la misma regla que la
  // casilla de arriba: con una búsqueda puesta, nada alcanza a lo que no se ve.
  // Publicar y descargar siguen yendo a TODO lo tildado: es lo que dice la barra.
  const paraElLote = useMemo(
    () => new Set(visibles.filter((p) => elegidos.has(p.id)).map((p) => p.id)),
    [visibles, elegidos],
  );

  /** Los productos que van al panel de IA, con lo que ya tienen para comparar. */
  const paraIa: ProductoParaIa[] = useMemo(
    () =>
      (idsParaIa ?? [])
        .map((id) => porId.get(id))
        .filter((p): p is ProductoDeLanzamiento => !!p)
        .map((p) => ({
          id: p.id,
          nombre: p.nombre,
          actual: {
            descripcion: p.descripcion,
            marca: p.marca,
            tags: p.tags,
            seoTitulo: p.seoTitulo,
            peso: p.peso,
            categorias: p.categorias,
          },
        })),
    [idsParaIa, porId],
  );

  const abrirIa = (ids: string[]) => {
    setIdsParaIa(ids);
    setSugerencias(new Map());
    setErrorIa(null);
    setProgresoIa(null);
  };

  const cerrarIa = () => {
    setIdsParaIa(null);
    setSugerencias(new Map());
    setErrorIa(null);
  };

  /**
   * Le pide a la IA los campos marcados, para uno o para todos los elegidos.
   *
   * De a uno y no en paralelo: son llamadas con fotos y razonamiento, y diez a
   * la vez chocan con el límite del proveedor y tardan más que en fila. El
   * progreso se muestra porque con cinco productos la espera se siente.
   *
   * Un producto que falla no corta el lote: se guarda lo que sí salió y al final
   * se dice cuántos quedaron afuera.
   */
  const pedirIa = (campos: CampoIa[]) => {
    if (!companyId || paraIa.length === 0) return;
    setPensando(true);
    setErrorIa(null);
    setProgresoIa({ hechos: 0, total: paraIa.length });
    void (async () => {
      const out = new Map<string, SugerenciaDeFicha>();
      let fallados = 0;
      for (const [i, item] of paraIa.entries()) {
        const p = porId.get(item.id)!;
        const r = await completarFichaConIa({
          nombre: p.nombre,
          descripcion: p.descripcion,
          rubro,
          marca: p.marca,
          variantes: [...p.variantes]
            .sort((a, b) => a.orden - b.orden)
            .map((v, j) => v.valores.filter(Boolean).join(' / ') || `Variante ${j + 1}`),
          categorias: cat.categorias.map((c) => ({
            id: c.id,
            camino: caminoDe(cat.categorias, c.id),
          })),
          campos,
          canal: detalle?.canal,
          // Las primeras: son la portada y las que mejor muestran el producto.
          fileNames: p.imagenes.slice(0, 4).map((im) => im.fileName),
        });
        if (r.sugerencia) out.set(item.id, r.sugerencia);
        else fallados += 1;
        setProgresoIa({ hechos: i + 1, total: paraIa.length });
      }
      setPensando(false);
      setProgresoIa(null);
      if (out.size === 0) {
        setErrorIa('No se pudo completar con IA');
        return;
      }
      if (fallados > 0) {
        setErrorIa(`${fallados} producto${fallados === 1 ? '' : 's'} falló y quedó sin sugerencias.`);
      }
      setSugerencias(out);
    })();
  };

  /** Guarda lo aceptado de cada producto. Es el mismo camino para uno o varios. */
  const aplicarIa = (aceptado: Map<string, CampoIa[]>) => {
    if (!companyId) return;
    despejarAviso();
    setPensando(true);
    void (async () => {
      let error: string | null = null;
      for (const [idProducto, campos] of aceptado) {
        const s = sugerencias.get(idProducto);
        const p = porId.get(idProducto);
        if (!s || !p || campos.length === 0) continue;
        const r = await aplicarSugerencia({
          productoId: idProducto,
          companyId,
          campos,
          sugerencia: s,
          yaTiene: p.categorias,
          categorias: cat.categorias,
          crearCategoria: cat.crear,
          unaSolaCategoria: reglasDelCanal.unaSolaCategoria,
        });
        error = error ?? r.error;
      }
      await cargar();
      await cat.cargar();
      setPensando(false);
      if (error) setErrorIa(error);
      else cerrarIa();
    })();
  };

  // ── Acciones en lote ──────────────────────────────────────────────────────

  const alternarElegido = (id: string) =>
    setExcluidos((s) => {
      const siguiente = new Set(s);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });

  /** Agrega una categoría a todos los seleccionados. */
  const categorizarEnLote = (categoriaId: string) => {
    if (!companyId || !categoriaEnLote) return;
    despejarAviso();
    const ids = categoriaEnLote;
    setEnLote(true);
    setErrorLote(null);
    void (async () => {
      let error: string | null = null;
      for (const id of ids) {
        const p = porId.get(id);
        if (!p || p.categorias.includes(categoriaId)) continue;
        const r = await marcarCategoriaDeProducto(id, categoriaId, companyId, true);
        error = error ?? r.error;
      }
      await cargar();
      setEnLote(false);
      if (error) setErrorLote(error);
      else setCategoriaEnLote(null);
    })();
  };

  const borrarEnLote = () => {
    if (!borradoEnLote) return;
    despejarAviso();
    const ids = new Set(borradoEnLote);
    setEnLote(true);
    void (async () => {
      for (const id of ids) await quitarProducto(id);
      // Si el que estaba abierto se fue, la ficha no puede quedar apuntándole.
      if (elegido && ids.has(elegido)) setElegido(null);
      setBorradoEnLote(null);
      setEnLote(false);
      await cargar();
    })();
  };

  /**
   * Baja un ZIP con las fotos ordenadas en carpetas
   * (ADR-2026-09-14-lanzamiento-a-carpetas).
   *
   * Es la "publicación" de un destino sin tienda, así que arranca con la MISMA
   * validación: un producto sin nombre armaría una carpeta llamada "producto", y
   * eso el usuario lo quiere saber antes de bajar 200 fotos, no después.
   *
   * NO toca el estado del lanzamiento: bajar un archivo no es publicar, y
   * marcarlo como publicado lo sacaría de "en preparación" sin que nada haya
   * salido a ningún lado. Se puede exportar las veces que haga falta.
   */
  const {
    chooserElement: chooserDeDescarga,
    resolverOpciones,
    aplicarOpciones,
  } = useOpcionesDeDescarga();

  /**
   * Manda un lote al lanzamiento y refresca la ficha. Es el cierre común de los dos caminos
   * de abajo: las creaciones con producto conocido y las que lo eligieron a mano.
   */
  const mandarAlLanzamiento = useCallback(
    async (items: ItemAEnviar[]) => {
      if (!detalle || !companyId || items.length === 0) return;
      // No rechaza hacia afuera: sus dos llamadores siguen con algo después (abrir el
      // selector de producto, cerrar la hoja) y una excepción acá les comía ese paso
      // (review Greptile #122). El error se cuenta en el aviso de la ficha.
      let resumen: string;
      let fallo = false;
      try {
        const r = await enviarImagenesALanzamiento(detalle.id, companyId, items);
        resumen = r.error ?? avisoDeSuma(r.agregadas, r.yaEstaban);
        fallo = !!r.error;
      } catch (e) {
        console.error('[lanzamiento] sumar creaciones', e);
        setAviso(falla('No se pudieron sumar las creaciones. Probá de nuevo.'));
        return;
      }
      setAviso(fallo ? falla(resumen) : info(resumen));
      // El refresco va APARTE del envío (review Greptile #122, 2ª vuelta): para cuando
      // corre, las filas YA están guardadas. Si recargar la ficha falla y eso se contara
      // como fallo del envío, se le diría "no se pudieron sumar" a alguien que SÍ las
      // sumó — y el reintento le contestaría "ya estaban", porque el envío es idempotente.
      // Lo único que se pierde es ver la lista al día, y eso es lo que dice el aviso.
      try {
        await cargar();
      } catch (e) {
        console.error('[lanzamiento] refrescar tras sumar', e);
        setAviso(falla(`${resumen} No se pudo refrescar la ficha: recargá la página para verlas.`));
      }
    },
    [detalle, companyId, cargar],
  );

  /**
   * Lo elegido en el selector de creaciones. Las que se generaron con un producto van a
   * ese producto; las demás pasan al selector de catálogo (`SelectorDeProducto`, el mismo
   * del botón "Enviar a lanzamiento"). Antes la ficha vacía sólo DECÍA que había que ir a
   * Creaciones, sin un botón para hacerlo, y la gente se iba sin sumar nada.
   */
  const sumarCreaciones = useCallback(
    async (elegidas: GeneradaParaElegir[]) => {
      const mapa = await productoPorVariante(
        elegidas.map((i) => i.varianteId).filter((v): v is string => !!v),
      );
      const { conProducto, sinProducto: pendientes } = repartirPorProducto(elegidas, mapa);
      // El `finally` es el punto: lo que falta asignar a mano NO se pierde aunque el envío
      // automático falle, y la hoja se cierra igual en vez de quedar con el botón colgado
      // —`SelectorDeCreaciones` no atrapa el rechazo de `onConfirmar`— (review Greptile #122).
      try {
        await mandarAlLanzamiento(conProducto);
      } finally {
        setEligiendoCreaciones(false);
        setSinProducto(pendientes);
      }
    },
    [mandarAlLanzamiento],
  );

  const exportarCarpetas = useCallback(async () => {
    if (!detalle || !companyId || publicando) return;
    setAviso(null);

    const aExportar = detalle.productos_.filter((p) => elegidos.has(p.id));
    if (aExportar.length === 0) {
      setAviso(falla('Tildá al menos un producto para descargar.'));
      return;
    }
    const problemas = problemasParaPublicar(aExportar, destinos);
    if (problemas.length > 0) {
      const p = problemas[0]!;
      setAviso(
        falla(
          problemas.length === 1
            ? `${p.nombre}: ${p.motivo}.`
            : `${problemas.length} productos sin completar. El primero, ${p.nombre}: ${p.motivo}.`,
        ),
      );
      setElegido(detalle.productos_.find((x) => x.productoId === p.productoId)?.id ?? elegido);
      return;
    }

    const plan = planDeExport(aExportar, cat.categorias);
    if (plan.length === 0) {
      setAviso(falla('No hay ninguna foto para exportar.'));
      return;
    }

    // El formato de salida se pregunta UNA vez para todo el ZIP y ANTES de firmar
    // nada (ADR-2026-09-14-configuracion-de-descarga): si cancela acá, no se firmó ni
    // se bajó un solo archivo. Es el lugar donde más se nota —este destino existe para
    // entregarle las fotos a una tienda, que es quien pide la medida y el peso.
    const opciones = await resolverOpciones({ cantidad: plan.length });
    if (opciones === null) return;

    setPublicando(true);
    setProgreso({ subidas: 0, total: plan.length });
    // Las firmas en paralelo —son llamadas cortas— y la bajada en serie, que es
    // la que tarda y la que el usuario ve avanzar.
    const archivos = (
      await Promise.all(
        plan.map(async (a) => {
          const url = await firmarFull(companyId, a.fileName);
          // `imagenId`: la creación queda marcada como descargada al salir en el zip. Una
          // foto subida a mano no tiene fila y va sin la clave.
          if (!url) return null;
          const archivo: ArchivoDeLote = { url, nombre: a.ruta };
          if (a.imagenGeneradaId) archivo.imagenId = a.imagenGeneradaId;
          return archivo;
        }),
      )
    ).filter((a): a is ArchivoDeLote => a !== null);

    const r = await descargarComoZip(
      archivos,
      nombreDelZip(detalle.nombre),
      (hechos, total) => setProgreso({ subidas: hechos, total }),
      opciones ? (blob) => aplicarOpciones(blob, false, opciones) : undefined,
    );
    setPublicando(false);
    setProgreso(null);
    if (r.ok === 0) {
      setAviso(falla('No se pudo bajar ninguna foto. Probá de nuevo en un momento.'));
    } else if (r.fallidos > 0 || archivos.length < plan.length) {
      const afuera = r.fallidos + (plan.length - archivos.length);
      setAviso(falla(`Se bajaron ${r.ok} fotos. ${afuera} quedaron afuera: probá exportar de nuevo.`));
    }
  }, [
    detalle,
    companyId,
    publicando,
    cat.categorias,
    elegido,
    elegidos,
    resolverOpciones,
    aplicarOpciones,
  ]);

  /** Publica: valida, firma las URLs, dispara el export y sigue el progreso. */
  const publicar = useCallback(async () => {
    if (!detalle || !companyId || publicando) return;
    setAviso(null);
    // El dueño puede no haberle habilitado la tienda (ADR-2026-10-19-permisos-por-miembro-del-equipo):
    // se avisa a quién pedírselo antes de validar nada.
    try {
      await exigirPermisoDeTienda();
    } catch (e) {
      setAviso(falla(e instanceof Error ? e.message : 'No tenés permiso para publicar en la tienda online.'));
      return;
    }

    // Salvavidas: se publica lo que está GUARDADO, no la copia que la pantalla cargó al abrir. Una
    // sección que guardó sin avisar (la de Mercado Libre), el chat u otra pestaña pueden haber
    // cambiado la ficha; sin esto, "Publicar" decía "falta la categoría" con la categoría ya elegida
    // (prueba del 2/10). Si la relectura falla, se sigue con lo que hay en pantalla.
    const fresco = (await fetchLanzamiento(detalle.id, companyId).catch(() => null)) ?? detalle;
    if (fresco !== detalle) setDetalle(fresco);
    // Las tiendas también salen de la ficha releída: si se cambiaron en otra pestaña, publicar
    // con las de la copia vieja mandaría a una que ya no está o saltearía una nueva (review #155).
    const destinosAhora = fresco.destinos;
    const sinPublicarAhora = destinosAhora.filter((d) => !canalDe(d).sePuedePublicar);

    // Primero lo que le falta a la ficha, con las reglas del DESTINO: para un
    // lanzamiento que todavía no se puede publicar, "Publicar" es la revisión de
    // que está todo, y eso vale aunque la conexión no exista
    // (ADR-2026-09-13-lanzamientos-para-vtex).
    // Sólo lo tildado (ADR-2026-09-30-lo-tildado-es-lo-que-se-publica): lo destildado no
    // se revisa ni se manda. Se filtra la ficha RELEÍDA por lo destildado y no por lo
    // tildado: un producto que se sumó desde otro lado (el chat, otra pestaña) entra
    // tildado, como en la tabla.
    const aPublicar = fresco.productos_.filter((p) => !excluidos.has(p.id));
    if (aPublicar.length === 0) {
      setAviso(falla('Tildá al menos un producto para publicar.'));
      return;
    }
    const tildado = { ...fresco, productos_: aPublicar };
    const problemas = problemasParaPublicar(aPublicar, destinosAhora);
    if (problemas.length > 0) {
      const p = problemas[0]!;
      setAviso(
        falla(
          problemas.length === 1
            ? `${p.nombre}: ${p.motivo}.`
            : `${problemas.length} productos sin completar. El primero, ${p.nombre}: ${p.motivo}.`,
        ),
      );
      setElegido(fresco.productos_.find((x) => x.productoId === p.productoId)?.id ?? elegido);
      return;
    }
    // Con varias tiendas se publica en las que ya se puede; la ficha ya quedó revisada contra todas.
    if (sinPublicarAhora.length === destinosAhora.length) {
      setAviso(
        info(
          `Todo listo para ${nombresDeDestinos(destinosAhora)}. La conexión para publicar todavía no está ` +
            'disponible: cuando la activemos, se publica desde este mismo botón.',
        ),
      );
      return;
    }

    // Lo propio de Mercado Libre, ANTES de mandar nada a ninguna tienda
    // (ADR-2026-09-30-la-ficha-de-mercado-libre): la cuenta conectada y la ficha completa.
    // Los pasos son los de `frenoDeMl`, los mismos que usa el chat.
    const vaAMl = destinosAhora.includes('mercadolibre') && canalDe('mercadolibre').sePuedePublicar;
    if (vaAMl) {
      const freno = frenoDeMl(tildado, ml.conectada);
      if (freno?.motivo === 'sin-cuenta') {
        setAviso(falla('Para publicar en Mercado Libre, conectá tu cuenta en Marca.'));
        return;
      }
      if (freno?.motivo === 'falta') {
        setAviso(falla(`${freno.producto.nombre}: ${freno.texto}.`));
        setElegido(freno.producto.id);
        return;
      }
    }

    setPublicando(true);
    let total = 0;
    let fotosEnviadas = 0;
    let salio = false;
    // Si una tienda falla y la otra sale, el aviso final (que mira los productos) diría "Listo" y
    // la falla desaparecería: se guarda para sumarla al final (review #155). "Ya se estaba
    // publicando" NO es una falla —la corrida anterior sigue y termina sola—: va aparte, sin
    // rojo, para que nadie reintente de más.
    let fallaDeUnaTienda: string | null = null;
    let enCursoEnUnaTienda: string | null = null;
    // Las tiendas que recibieron ESTA tanda: con una que no, el final nombra la que sí.
    const salioEn: CanalLanzamiento[] = [];
    if (destinosAhora.includes('tiendanube')) {
      // Los pasos viven en `publicarLanzamiento` desde
      // ADR-2026-09-21-publicar-un-lanzamiento-desde-el-chat: los mismos de antes, en el
      // mismo orden, pero fuera de la pantalla para que el chat también pueda publicar.
      // El árbol de categorías se le pasa como función: acá el estado ya está cargado, y
      // desde el chat se lee de la base en el momento.
      const r = await publicarLanzamiento(tildado, {
        estadoDeLaTienda: () => getTnStatus(brandDelLanzamiento),
        firmar: (fileName) => firmarFull(companyId, fileName),
        categorias: async () => cat.categorias,
        publicados: (ids) => leerPublicadosEnTn(brandDelLanzamiento, ids),
        disparar: (productos, lanzamientoId) =>
          fireTnExport({ companyId, brandId: brandDelLanzamiento, productos, lanzamientoId }),
      });
      // Si una tienda no puede, la otra sigue: con Mercado Libre también elegida, se avisa y se sigue.
      if (r.estado === 'sin-tienda' && !vaAMl) {
        setPublicando(false);
        setPedirConexion(true);
        return;
      }
      if (r.estado === 'sin-fotos') {
        setPublicando(false);
        setAviso(falla('No se pudo preparar ninguna foto para subir.'));
        return;
      }
      // ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento
      if (r.estado === 'sin-cambios' && !vaAMl) {
        setPublicando(false);
        setAviso(info('No hay nada para publicar: esos productos ya están así en tu tienda.'));
        return;
      }
      if (r.estado === 'sin-lectura' && !vaAMl) {
        setPublicando(false);
        setAviso(falla('No se pudo leer cómo están los productos en tu tienda. No se cambió nada; probá de nuevo en un rato.'));
        return;
      }
      if (r.estado === 'faltan') {
        // No debería llegar acá (se revisó arriba), pero si llega, no se sigue de largo.
        setPublicando(false);
        setAviso(falla(`${r.problemas[0]!.nombre}: ${r.problemas[0]!.motivo}.`));
        return;
      }
      if (r.estado === 'rechazado' && r.motivo === YA_PUBLICANDO && !vaAMl) {
        setPublicando(false);
        setAviso(info('Ya se está publicando en Tiendanube. Esperá a que termine: no lo mandé de nuevo para no duplicar.'));
        void cargar();
        return;
      }
      if (r.estado === 'rechazado' && !vaAMl) {
        setPublicando(false);
        setAviso(
          falla(
            copyDeErrorDePermiso(r.motivo) ??
              'No se pudo publicar. En tu tienda no se creó ni se subió nada; probá de nuevo.',
          ),
        );
        void cargar();
        return;
      }
      if (r.estado === 'lanzado') {
        total = r.productos;
        fotosEnviadas = r.imagenes;
        salio = true;
        salioEn.push('tiendanube');
      } else {
        if (r.estado === 'rechazado' && r.motivo === YA_PUBLICANDO) {
          // La corrida anterior termina sola, pero puede ser de OTROS productos: esta tanda no
          // llegó a Tiendanube, y eso se dice sin rojo (no falló nada).
          enCursoEnUnaTienda =
            'Tiendanube no recibió esta tanda porque ya había otra publicación en curso. Cuando termine, si falta algún producto, publicá de nuevo';
        } else {
          fallaDeUnaTienda =
            r.estado === 'sin-tienda'
              ? 'Tiendanube no está conectada'
              : r.estado === 'sin-cambios'
                ? 'En Tiendanube no había nada para cambiar'
                : r.estado === 'sin-lectura'
                  ? 'En Tiendanube no se pudo leer cómo están los productos (no se cambió nada)'
                  : 'Tiendanube no lo aceptó (ahí no se creó nada)';
        }
        setAviso(
          (enCursoEnUnaTienda ? info : falla)(
            r.estado === 'sin-tienda'
              ? 'Tiendanube no está conectada: se publica sólo en Mercado Libre.'
              : r.estado === 'rechazado' && r.motivo === YA_PUBLICANDO
                ? 'En Tiendanube ya se está publicando (no se manda de nuevo); se publica en Mercado Libre.'
                : r.estado === 'sin-cambios'
                  ? 'En Tiendanube ya están así; se publica en Mercado Libre.'
                  : r.estado === 'sin-lectura'
                    ? 'No se pudo leer cómo están en Tiendanube (ahí no se cambió nada); se publica en Mercado Libre.'
                    : 'Tiendanube no lo aceptó (ahí no se creó nada); se publica en Mercado Libre.',
          ),
        );
      }
    }
    if (vaAMl) {
      // Una publicación por color × talle; el backend anota cómo le fue a cada producto.
      const r = await publicarLanzamientoEnMl(tildado, {
        cuentaConectada: ml.conectada,
        publicar: (productos, lanzamientoId) => publicarEnMl({ brandId: brandDelLanzamiento, lanzamientoId, productos }),
      });
      if (r.motivo === 'lanzado') {
        total = Math.max(total, r.productos);
        salio = true;
        salioEn.push('mercadolibre');
      } else if (r.motivo === 'rechazado') {
        fallaDeUnaTienda = `Mercado Libre: ${r.texto}`;
        setAviso(falla(`Mercado Libre: ${r.texto}.`));
      } else if (r.motivo === 'sin-fotos') {
        fallaDeUnaTienda = 'Mercado Libre: ningún producto tiene fotos para publicar';
        setAviso(falla('Mercado Libre: ningún producto tiene fotos para publicar.'));
      }
    }
    if (!salio) {
      setPublicando(false);
      void cargar();
      return;
    }
    await cargar();

    setProgreso({ subidas: 0, total });
    const desde = Date.now();
    const limite = limiteDeEsperaMs({ productos: total, fotos: fotosEnviadas });
    pollRef.current = window.setInterval(async () => {
      const r = await revisarPublicacion(fresco.id, companyId);
      setProgreso({ subidas: r.subidas, total: r.total || total });
      if (r.terminado) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        // Primero la ficha nueva y después el aviso: si no, "Listo" convivía unos segundos con la fila
        // que todavía decía "se va a crear" (prueba del 2/10). Si la relectura falla, el aviso sale igual.
        await cargar().catch(() => {});
        // Lo publicado cambió: se vuelve a comparar contra lo de ahora.
        setVueltaDeLectura((v) => v + 1);
        setPublicando(false);
        setProgreso(null);
        setAviso(
          (r.conErrores || fallaDeUnaTienda ? falla : info)(
            (r.conErrores
              ? r.motivo
                ? `Terminó, pero algunos productos no se pudieron publicar. ${r.motivo.replace(/\.$/, '')}. Corregilo y probá de nuevo.`
                : 'Terminó, pero algunos productos no se pudieron publicar. Mirá cada uno y probá de nuevo.'
              : fallaDeUnaTienda || enCursoEnUnaTienda
                ? `Se publicó en ${nombresDeDestinos(salioEn)}.`
                : aPublicar.length === fresco.productos_.length
                  ? 'Listo, se publicaron todos los productos.'
                  : aPublicar.length === 1
                    ? 'Listo, se publicó el producto tildado.'
                    : `Listo, se publicaron los ${aPublicar.length} productos tildados.`) +
              (fallaDeUnaTienda ? ` ${fallaDeUnaTienda}.` : '') +
              (enCursoEnUnaTienda ? ` ${enCursoEnUnaTienda}.` : '') +
              (sinPublicarAhora.length > 0
                ? ` En ${nombresDeDestinos(sinPublicarAhora)} todavía no se puede publicar desde Atlas: la ficha ya quedó revisada.`
                : ''),
          ),
        );
        // El árbol TAMBIÉN: el worker le escribió el `tn_category_id` a las
        // categorías que acaba de crear en la tienda. Sin recargarlo, publicar
        // una segunda vez sin refrescar la página las mandaría otra vez en null
        // y se crearían DUPLICADAS allá.
        void cat.cargar();
        return;
      }
      if (Date.now() - desde > limite) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        // Si el backend sigue publicando, no es un error: se sigue mirando sin el botón trabado.
        if (await sigueEnElBackend(fresco.id)) {
          setPublicando(false);
          setProgreso(null);
          setAviso(info('Sigue publicándose: los productos se van marcando solos. Podés seguir trabajando.'));
          void cargar();
          return;
        }
        await marcarPublicacionIncompleta(fresco.id);
        setPublicando(false);
        setProgreso(null);
        setAviso(falla('Algunas fotos no se confirmaron. Podés volver a publicar.'));
        void cargar();
        // Ídem: puede haber alcanzado a crear algunas antes de quedarse corto.
        void cat.cargar();
      }
    }, POLL_MS);
  }, [detalle, companyId, brandDelLanzamiento, cat, publicando, cargar, elegido, excluidos, ml.conectada]);

  // Recorridos de la primera vez (ADR-2026-10-01-recorridos-de-tienda-online). Antes
  // del `return` temprano: un hook no puede quedar detrás de un `if`. El calendario de
  // Instagram es otra pantalla y no los lleva.
  const hayProductos = (detalle?.productos_.length ?? 0) > 0;
  const cuenta = useMemo(() => contarEstados(detalle?.productos_ ?? [], destinos), [detalle?.productos_, destinos]);
  useRecorrido(
    'tienda_tour_detalle',
    !cargando && !!detalle && !esCalendario && hayProductos && !producto,
    () => pasosDelLanzamiento(exportaCarpetas),
  );
  useRecorrido(
    'tienda_tour_vacio',
    !cargando && !!detalle && !esCalendario && !hayProductos,
    pasosDelLanzamientoVacio,
  );

  // Teléfono (ADR-2026-10-16-el-detalle-del-lanzamiento-en-el-telefono): una sola flecha, la
  // flotante del header, y Borrar y Publicar a su derecha. Eran tres "volver" (la flotante, el
  // chevron del título y el "Volver" de la ficha) y las acciones le comían el ancho al nombre.
  // La flecha vuelve UN paso: con una ficha abierta, a la lista del lanzamiento; si no, a la
  // pantalla anterior con `useVolverAtras`, que cae en Tienda online si se entró por link
  // (nunca `window.history.length`: cuenta la pestaña entera y sacaba de Atlas,
  // ADR-2026-10-16-review-186-la-flecha-no-sale-de-atlas).
  const { setMobileBackOverride, setMobileRightAction } = useSidebar();
  const volverAtras = useVolverAtras('/lanzamientos');
  const volverAtrasRef = useRef(volverAtras);
  volverAtrasRef.current = volverAtras;
  // El header flotante un poco más abajo en esta pantalla: con Borrar y Publicar al lado de la
  // flecha, la fila entera pegada al borde de arriba se veía apretada. El shell y el `main` leen
  // la variable; al salir se saca y las otras pantallas quedan como estaban.
  useEffect(() => {
    document.documentElement.style.setProperty('--aire-header-mobile', '12px');
    return () => {
      document.documentElement.style.removeProperty('--aire-header-mobile');
    };
  }, []);
  const fichaAbiertaRef = useRef(false);
  fichaAbiertaRef.current = !!producto;
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (fichaAbiertaRef.current) setElegido(null);
      else volverAtrasRef.current();
    });
    return () => setMobileBackOverride(null);
  }, [setMobileBackOverride]);
  // Los handlers cambian de identidad en cada render: por ref, como "Desactivar" de Creaciones,
  // o el efecto re-setearía el header en cada render.
  const accionesRef = useRef({ publicar, exportarCarpetas });
  accionesRef.current = { publicar, exportarCarpetas };
  const listo = !cargando && !!detalle;
  useEffect(() => {
    if (!listo) return;
    setMobileRightAction(
      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPedirBorrado(true)}
          aria-label="Borrar lanzamiento"
          title="Borrar lanzamiento"
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/95 text-red-600 shadow-md ring-1 ring-black/[0.04] backdrop-blur-md transition-all active:brightness-95"
        >
          <Trash2 className="h-5 w-5" />
        </button>
        {!esCalendario && (
          <button
            type="button"
            onClick={() =>
              void (exportaCarpetas ? accionesRef.current.exportarCarpetas() : accionesRef.current.publicar())
            }
            disabled={publicando || elegidos.size === 0}
            data-tour="tienda-publicar"
            className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-brand-primary px-5 text-[15px] font-medium text-white shadow-md transition-all active:brightness-95 disabled:opacity-50"
          >
            {publicando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : exportaCarpetas ? (
              <Download className="h-4 w-4" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {publicando
              ? progreso
                ? `${progreso.subidas}/${progreso.total}`
                : 'Preparando…'
              : exportaCarpetas
                ? 'ZIP'
                : 'Publicar'}
            {!publicando && elegidos.size > 0 && (
              <span className="rounded-md bg-white/20 py-0.5 px-1.5 text-xs tabular-nums">{elegidos.size}</span>
            )}
          </button>
        )}
      </div>,
    );
    return () => setMobileRightAction(null);
  }, [listo, esCalendario, exportaCarpetas, publicando, progreso, elegidos.size, setMobileRightAction]);

  if (cargando) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
      </div>
    );
  }

  if (!detalle) {
    return (
      <div className="mx-auto max-w-lg py-24 px-6 text-center">
        <AlertCircle className="mx-auto w-8 h-8 text-neutral-300" />
        <p className="mt-4 text-sm text-brand-black">Este lanzamiento no existe</p>
        <button
          type="button"
          onClick={() => navigate('/lanzamientos')}
          className="mt-4 text-sm text-neutral-500 underline"
        >
          Volver a lanzamientos
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-4 px-4 md:py-8 md:px-8">
      {/* La hoja que pregunta el formato de salida antes de armar el ZIP. Se dibuja en
          un portal: el lugar en el árbol no importa, sólo que esté montada. */}
      {chooserDeDescarga}
      {/* El chat se abre desde el botón flotante del cascarón, que ya sabe por la
          ruta que está en la ficha de un lanzamiento; y cuál es lo saca de la URL
          (`lanzamientoParaElAgente`), no de un texto que le pase esta pantalla.
          Un botón propio acá era un cuarto control en el renglón del título
          (ADR-2026-09-15-el-volver-va-pegado-al-titulo). */}
      {/* ⚠ EL `<h1>` TIENE QUE SER HIJO DIRECTO DE ESTA FILA. NO LO ENVUELVAS.
          `index.css` tiene una regla global con `!important` que le da 40px de
          aire arriba al PADRE DIRECTO de cualquier `<h1>` del shell
          (`.shell-seccion :has(> h1…)`), para que todas las secciones abran
          igual. Si el título cuelga de un div propio, esos 40px se los lleva ese
          div y el título baja 40px mientras las acciones se quedan arriba: es
          justo el desalineado que hubo
          (ADR-2026-09-15-el-titulo-no-se-puede-envolver).

          Por eso el chevron y las acciones son HERMANOS del título, no vecinos
          de un wrapper: el título crece con `flex-1` y empuja las acciones a la
          derecha, sin `justify-between` ni contenedores de por medio. */}
      <div className="flex flex-wrap items-center gap-y-3">
        <button
          type="button"
          onClick={() => navigate('/lanzamientos')}
          aria-label="Volver a Lanzamientos"
          title="Volver a Lanzamientos"
          // Debajo de `md` vuelve la flecha flotante del header (ver `setMobileBackOverride`).
          className="-ml-1 mr-1 hidden h-7 w-7 shrink-0 place-items-center rounded-lg text-brand-black hover:bg-neutral-100 md:grid"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold text-brand-black md:text-2xl">
          {detalle.nombre}
        </h1>
        {/* Debajo de `md` Borrar y Publicar van en el header flotante (`setMobileRightAction`). */}
        <div className="ml-4 hidden shrink-0 items-center gap-2 md:flex">
          {/* El chat se abre desde el flotante de la esquina inferior derecha,
              que en esta pantalla aparece SIEMPRE aunque la empresa no haya
              prendido el acceso rápido
              (ADR-2026-09-15-en-un-lanzamiento-el-flotante-no-es-opcional). Acá
              no va un botón propio: el encabezado ya tiene sus acciones, y el
              flotante no se puede montar desde una pantalla —un `fixed` de
              página queda debajo de la sidebar—, así que vive en el cascarón. */}
          {/* Un post no va en una categoría de la tienda. */}
          {!esCalendario && (
            <button
              type="button"
              onClick={() => setVerCategorias(true)}
              data-tour="tienda-categorias"
              className={boton('secundario', { extra: 'hidden sm:inline-flex' })}
            >
              <FolderTree className="h-4 w-4" />
              Categorías
            </button>
          )}
          <button
            type="button"
            onClick={() => setPedirBorrado(true)}
            aria-label="Borrar lanzamiento"
            title="Borrar lanzamiento"
            className={boton('peligro', { extra: 'px-3 sm:px-4' })}
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Borrar</span>
          </button>
          {/* El interruptor de qué pasa a la hora de cada post. ESCONDIDO hasta
              que el publicador exista: hoy las dos opciones hacen lo mismo
              (ADR-2026-09-16-si-las-dos-opciones-hacen-lo-mismo-no-hay-que-preguntar).
              No se borra nada: cuando `sePuedeElegirQuePasaALaHora` devuelva
              `true`, vuelve entero. */}
          {esCalendario && sePuedeElegirQuePasaALaHora() && (
            <ToggleDeModo modo={modoRedes} onCambiar={cambiarModoRedes} />
          )}
          {/* El mismo botón dice lo que va a pasar: subir a una tienda, o bajar
              un ZIP. Un "Publicar" que en realidad descarga un archivo sería
              mentirle al que lo aprieta. En el calendario no hay botón: lo que
              publica es la hora de cada post, no una acción del lanzamiento. */}
          {!esCalendario && (
          <button
            type="button"
            onClick={() => void (exportaCarpetas ? exportarCarpetas() : publicar())}
            disabled={publicando || elegidos.size === 0}
            data-tour="tienda-publicar"
            title={
              elegidos.size === 0
                ? 'Tildá al menos un producto'
                : exportaCarpetas
                  ? `Descarga ${elegidos.size === 1 ? 'el producto tildado' : `los ${elegidos.size} productos tildados`} en un ZIP`
                  : `Publica ${elegidos.size === 1 ? 'el producto tildado' : `los ${elegidos.size} productos tildados`} en ${nombresDeDestinos(destinos)}`
            }
            className={boton('primario')}
          >
            {publicando ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : exportaCarpetas ? (
              <Download className="w-4 h-4" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {publicando
              ? progreso
                ? `${exportaCarpetas ? 'Descargando' : 'Publicando'} ${progreso.subidas}/${progreso.total}`
                : exportaCarpetas
                  ? 'Preparando…'
                  : 'Publicando…'
              : exportaCarpetas
                ? 'Descargar ZIP'
                : 'Publicar'}
            {/* Cuántos salen, a la vista: lo tildado es lo que se publica
                (ADR-2026-09-30-lo-tildado-es-lo-que-se-publica). */}
            {!publicando && elegidos.size > 0 && (
              <span className="rounded-md bg-white/20 py-0.5 px-1.5 text-xs tabular-nums">{elegidos.size}</span>
            )}
          </button>
          )}
        </div>
      </div>
      {esCalendario ? (
        <p className="mt-1 text-sm md:pl-7 text-neutral-500">
          {nombreCanal(detalle.canal)} · {detalle.productos} post
          {detalle.productos === 1 ? '' : 's'} planificado
          {detalle.productos === 1 ? '' : 's'}
        </p>
      ) : producto ? null : (
        /* Con una ficha abierta no va: la ficha tiene su propio estado, y el panel la
           empujaba media pantalla para abajo. A qué tienda va, si está conectada y en qué paso está el lanzamiento: reemplaza al
           renglón gris de "Tienda Nube · 3 productos · 4 fotos" y al chip de "Tiendas"
           (ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta). Las tiendas se siguen sumando
           o sacando desde acá (ADR-2026-09-30-lanzamiento-a-varias-tiendas). */
        <PanelDeLaTienda
          destinos={destinos}
          opciones={
            esDeTiendas
              ? DESTINOS_QUE_CREAN.filter((d) => d !== 'mercadolibre' || ml.disponible || destinos.includes(d))
              : []
          }
          exportaCarpetas={exportaCarpetas}
          sinPublicar={sinPublicar}
          conexiones={conexiones}
          cuenta={cuenta}
          total={detalle.productos_.length}
          fotos={detalle.imagenes}
          bloqueado={publicando || cambiandoDestinos}
          onConectarTiendanube={() => setPedirConexion(true)}
          onCambiarDestinos={(d) => void cambiarDestinos(d)}
        />
      )}

      {aviso && (
        <div
          role={aviso.error ? 'alert' : 'status'}
          className={`mt-4 flex items-start gap-2 rounded-xl border py-3 px-4 text-sm ${
            aviso.error
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-neutral-200 bg-neutral-50 text-brand-black'
          }`}
        >
          <AlertCircle
            className={`mt-0.5 w-4 h-4 shrink-0 ${aviso.error ? 'text-red-500' : 'text-neutral-400'}`}
          />
          <span>{aviso.texto}</span>
        </div>
      )}

      {esCalendario ? (
        companyId && (
          <CalendarioDeRedes
            lanzamientoId={detalle.id}
            companyId={companyId}
            fechaLanzamiento={detalle.fechaLanzamiento}
            aceptaVideo={reglasDelCanal.conVideo}
            onCambio={cargar}
          />
        )
      ) : detalle.productos_.length === 0 ? (
        <EstadoVacio onElegir={() => setEligiendoCreaciones(true)} onIrACreaciones={() => navigate('/creaciones')} />
      ) : producto ? (
        /* La ficha ocupa todo el ancho y la tabla se va mientras tanto: la barra de arriba
           hace lo que hacía la columna de la izquierda —volver, saber en cuál se está y
           pasar a otro— sin quitarle dos tercios a la ficha
           (ADR-2026-10-06-la-ficha-ocupa-la-pantalla). La `key` en el envoltorio y no en
           la ficha para que la animación de entrada se vuelva a correr al cambiar de
           producto: sin remontar, la clase ya estaría aplicada. */
        <div ref={inicioDeLaFicha} className="mt-6">
          {/* Pegada arriba al scrollear en escritorio: la ficha es larga y las flechas
              tienen que estar a mano sin volver a subir. En el teléfono no: la barra ocupa
              dos renglones y taparía un tercio de la pantalla. */}
          <div className="lg:sticky lg:top-3 lg:z-20">
              <BarraDeLaFicha
              productos={detalle.productos_}
              actual={producto}
              estadoDe={(p) => estadoDeFila(p, destinos, exportaCarpetas ? 'descargar' : 'publicar')}
              onElegir={setElegido}
              onVolver={() => setElegido(null)}
            />
          </div>
          <div key={producto.id} className="animate-ficha-entra mt-4">
          <FichaDeProducto
            producto={producto}
            companyId={companyId}
            canal={detalle.canal}
            destinos={destinos}
            brandId={brandDelLanzamiento}
            rubro={rubro}
            onCambio={async () => {
              despejarAviso();
              await cargar();
            }}
            // Lo que guarda la sección de Mercado Libre se refleja en la copia de la pantalla sin
            // recargar todo (el resumen de la ficha y "Publicar" la leen).
            onDatosMl={(lanzamientoProductoId, datos) => {
              despejarAviso();
              setDetalle((d) => conDatosMl(d, lanzamientoProductoId, datos));
            }}
            // Reordenar y quitar NO recargan el lanzamiento: la ficha ya
            // aplicó el cambio en pantalla y acá solo se persiste. Recargar
            // volvería a firmar todas las miniaturas y el navegador las
            // recargaría, que es el parpadeo que se veía al soltar una foto.
            onQuitarImagen={(idImagen) => {
              despejarAviso();
              return quitarImagen(idImagen);
            }}
            onReordenarImagenes={(ids) => {
              despejarAviso();
              return reordenarImagenes(ids);
            }}
            categorias={cat.categorias}
            cargandoCategorias={cat.cargando}
            onAbrirCategorias={() => setVerCategorias(true)}
            onCompletarConIa={() => abrirIa([producto.id])}
            comentariosPendientes={pendientes.get(producto.id) ?? 0}
            onAbrirComentarios={() => setVerComentarios(true)}
            lecturaDeLaTienda={lecturaDe(producto)}
            onReleerTienda={() => setVueltaDeLectura((v) => v + 1)}
          />
          </div>
        </div>
      ) : (
        <div className="mt-6">
        <div
          data-tour="tienda-tabla"
          className="overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm"
        >
          {/* El buscador siempre: con todo tildado de entrada, si la barra lo
              reemplazara no se podría buscar nunca. */}
          <Buscador
            valor={consulta}
            onCambiar={setConsulta}
            cantidad={detalle.productos_.length}
            onAgregar={() => setEligiendoCreaciones(true)}
          />
          {elegidos.size > 0 && (
            <BarraDeSeleccion
              cantidad={elegidos.size}
              total={detalle.productos_.length}
              aLaVista={paraElLote.size}
              verbo={exportaCarpetas ? 'descargar' : 'publicar'}
              onLimpiar={() => setExcluidos(new Set(detalle.productos_.map((p) => p.id)))}
              onCompletarConIa={() => abrirIa([...paraElLote])}
              onCategorizar={() => setCategoriaEnLote([...paraElLote])}
              onBorrar={() => setBorradoEnLote([...paraElLote])}
            />
          )}
          <EncabezadoDeTabla
            modo={modoDeTabla}
            todos={visibles.length > 0 && visibles.every((p) => elegidos.has(p.id))}
            algunos={visibles.some((p) => elegidos.has(p.id))}
            // "Todos" es todos los VISIBLES: con una búsqueda puesta, tildar
            // la casilla de arriba no puede alcanzar a lo que no se ve.
            onTodos={(v) =>
              setExcluidos((s) => {
                const siguiente = new Set(s);
                for (const p of visibles) {
                  if (v) siguiente.delete(p.id);
                  else siguiente.add(p.id);
                }
                return siguiente;
              })
            }
          />
          <div className="divide-y divide-neutral-100">
            {visibles.map((p) => (
              <FilaDeProducto
                key={p.id}
                producto={p}
                estado={estadoDeFila(p, destinos, exportaCarpetas ? 'descargar' : 'publicar')}
                modo={modoDeTabla}
                categorias={cat.categorias}
                cambiosEnLaTienda={cambiosPorProducto.get(p.id) ?? null}
                elegido={elegidos.has(p.id)}
                onAbrir={() => setElegido(p.id)}
                onElegir={() => alternarElegido(p.id)}
              />
            ))}
          </div>
          {visibles.length === 0 && (
            <p className="py-10 px-4 text-center text-sm text-neutral-500">
              Ningún producto coincide con “{consulta.trim()}”.
            </p>
          )}
        </div>
        </div>
      )}

      {/* Sólo aparece al intentar poner "Publicar automáticamente" sin cuenta:
          el aviso llega cuando la falta estorba, no como un cartel permanente. */}
      <ModalConectarInstagram
        abierto={pedirInstagram}
        onCerrar={() => setPedirInstagram(false)}
      />

      <ModalConfirmar
        abierto={pedirBorrado}
        titulo="¿Borrar este lanzamiento?"
        detalle="Se borra el lanzamiento con sus productos y el orden de las fotos. Las imágenes generadas NO se borran: siguen en tus creaciones."
        confirmar="Borrar lanzamiento"
        trabajando={borrando}
        onCancelar={() => setPedirBorrado(false)}
        onConfirmar={async () => {
          setBorrando(true);
          await borrarLanzamiento(detalle.id);
          navigate('/lanzamientos');
        }}
      />

      <PanelDeComentarios
        abierto={verComentarios && !!producto}
        nombreDelProducto={producto?.nombre ?? ''}
        comentarios={com.comentarios}
        cargando={com.cargando}
        trabajando={com.trabajando}
        error={com.error}
        onCerrar={() => {
          setVerComentarios(false);
          // El contador del botón sale del lanzamiento, así que hay que volver a
          // pedirlo: si no, se comenta, se cierra y el número sigue en cero.
          void cargar();
        }}
        onComentar={(t) => void com.dejar(t)}
        onResolver={(idC, r) => void com.resolver(idC, r)}
        onBorrar={(idC) => void com.borrar(idC)}
      />

      <PanelDeIa
        abierto={idsParaIa !== null}
        onCerrar={cerrarIa}
        productos={paraIa}
        trabajando={pensando}
        progreso={progresoIa}
        error={errorIa}
        sugerencias={sugerencias}
        categorias={cat.categorias}
        camposOfrecidos={camposParaCanal(detalle?.canal)}
        onPedir={pedirIa}
        onAplicar={aplicarIa}
      />

      <SheetDeCategoria
        abierto={!!categoriaEnLote}
        cantidad={categoriaEnLote?.length ?? 0}
        categorias={cat.categorias}
        trabajando={enLote}
        error={errorLote}
        onCerrar={() => setCategoriaEnLote(null)}
        onElegir={categorizarEnLote}
        onCrearYElegir={(nombre, madre) => {
          setEnLote(true);
          setErrorLote(null);
          void (async () => {
            const r = await cat.crear(nombre, madre);
            setEnLote(false);
            if (!r.id) setErrorLote(r.error);
            else categorizarEnLote(r.id);
          })();
        }}
      />

      <ModalConfirmar
        abierto={!!borradoEnLote}
        titulo={`¿Sacar ${borradoEnLote?.length ?? 0} producto${borradoEnLote?.length === 1 ? '' : 's'}?`}
        detalle="Salen de este lanzamiento con sus fotos. No se borran del catálogo ni se pierde ninguna imagen."
        confirmar="Sacar del lanzamiento"
        trabajando={enLote}
        onCancelar={() => setBorradoEnLote(null)}
        onConfirmar={borrarEnLote}
      />

      <PanelDeCategorias
        abierto={verCategorias}
        categorias={cat.categorias}
        cargando={cat.cargando}
        onCerrar={() => {
          setVerCategorias(false);
          // Borrar una categoría desata a los productos que la tenían: sin esto,
          // la ficha seguiría mostrándola tildada hasta recargar la pantalla.
          void cargar();
        }}
        onCrear={cat.crear}
        onRenombrar={cat.renombrar}
        onMover={cat.mover}
        onBorrar={cat.borrar}
      />

      {/* Sumar creaciones desde la ficha: el selector de siempre y, para las que no salieron
          de un producto, el de catálogo. Fuera del ternario del vacío a propósito: la ficha
          deja de estar vacía en cuanto entra la primera y el de catálogo tiene que seguir. */}
      <SelectorDeCreaciones
        abierta={eligiendoCreaciones}
        companyId={companyId}
        // Se abre desde el estado vacío y desde "Agregar productos" de la tabla: con
        // productos adentro, no vuelve a ofrecer las fotos que ya están. Array nuevo por
        // pintado, que es justo lo que `SelectorDeCreaciones` ya contempla (`yaUsadas` no
        // entra en las deps de su efecto).
        yaUsadas={detalle.productos_.flatMap((p) =>
          p.imagenes.map((i) => i.imagenGeneradaId).filter((id): id is string => !!id),
        )}
        conVideo={reglasDelCanal.conVideo}
        filtroDeProducto={{ brandId: brandDelLanzamiento }}
        onCerrar={() => setEligiendoCreaciones(false)}
        onConfirmar={sumarCreaciones}
      />
      <SelectorDeProducto
        abierta={sinProducto.length > 0}
        brandId={brandDelLanzamiento}
        onCerrar={() => setSinProducto([])}
        onElegir={(productoId) => {
          const pendientes = sinProducto;
          setSinProducto([]);
          void mandarAlLanzamiento(itemsParaProducto(pendientes, productoId));
        }}
      />
      <TiendaNubeConnectModal
        open={pedirConexion}
        companyHasStores={false}
        connecting={conectando}
        error={errorConexion}
        explanation="Conectá tu tienda para publicar los productos de este lanzamiento."
        onCancel={() => {
          setErrorConexion(null);
          setPedirConexion(false);
        }}
        onConnect={conectarTienda}
      />
    </div>
  );
}

/**
 * Confirmación destructiva dentro de la app. Reemplaza a `window.confirm`, que en
 * mobile aparece pegado arriba, no se puede escribir y saca al usuario de la app.
 * Portaleado a `document.body`: el panel de contenido de escritorio es `position:
 * fixed` y un overlay montado adentro queda por debajo de la barra lateral.
 */

/**
 * Ancho de cada columna de la fila. El encabezado y las filas comparten las
 * constantes —y van inline y no en clases— para que no haya forma de tocar una
 * y que la otra quede desalineada.
 *
 * Los datos van todos alineados a la IZQUIERDA y con anchos parecidos. Con
 * stock y precio a la derecha de su columna y el SKU a la izquierda de la suya,
 * los huecos ópticos quedaban de 162px, 24px y 376px: precio y SKU pegados, un
 * vacío enorme antes del botón. Alineados igual, cada hueco es "lo que le sobra
 * a la columna" y las tres sobran parecido.
 *
 * La última columna es la que se estira (`0.5fr`), no la del nombre: si el
 * nombre se quedara con todo el espacio libre, los datos se irían al borde
 * derecho y quedarían lejísimos del producto al que describen. Estirando la de
 * la acción, los datos se sientan al lado del nombre y el único hueco grande —el
 * que separa los datos del botón— se lee como intencional.
 *
 * La tabla ya no se achica al costado de la ficha: la ficha ocupa la pantalla
 * (ADR-2026-10-06-la-ficha-ocupa-la-pantalla), así que hay una sola forma.
 */
const COLUMNAS = '28px minmax(0px,1fr) 96px 120px 150px minmax(264px,0.6fr)';

/**
 * Un lanzamiento que exporta carpetas no vende: stock, precio y SKU no existen
 * (ADR-2026-09-14-lanzamiento-a-carpetas). En su lugar se ven las dos cosas que
 * deciden dónde cae cada foto en el ZIP: la categoría y las variantes.
 *
 * Son DOS columnas y no tres, así que la grilla tiene cinco tracks en vez de
 * seis. No se mezclan nunca: un lanzamiento tiene un solo destino.
 */
const CARPETAS = '28px minmax(0px,1fr) 180px 180px minmax(264px,0.6fr)';

/** Qué muestra la tabla: lo que se vende, o lo que arma las carpetas. */
type ModoDeTabla = 'tienda' | 'mercadolibre' | 'carpetas';

function estiloDeFila(modo: ModoDeTabla) {
  return { gridTemplateColumns: modo === 'carpetas' ? CARPETAS : COLUMNAS, columnGap: '1rem' };
}

/**
 * El buscador de la tabla. Va DENTRO de la card, arriba del encabezado de
 * columnas: es parte de la tabla, no una barra de herramientas de la pantalla.
 *
 * Busca contra todo lo que identifica al producto —nombre, SKU, código de
 * barras, marca, etiquetas, valores de las variantes y el camino de sus
 * categorías—, así que el placeholder no promete un campo en particular.
 *
 * Al lado va "Agregar productos": abre el mismo selector de creaciones que la
 * pantalla vacía. Sin él, un lanzamiento con productos no tenía cómo sumar más
 * desde acá (ADR-2026-10-20-agregar-productos-a-un-lanzamiento-con-productos).
 */
function Buscador({
  valor,
  onCambiar,
  cantidad,
  onAgregar,
}: {
  valor: string;
  onCambiar: (v: string) => void;
  cantidad: number;
  onAgregar: () => void;
}) {
  return (
    <div className="flex items-center border-b border-neutral-200 pr-3">
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-4 top-1/2 w-4 h-4 -translate-y-1/2 text-neutral-400" />
        <input
          // `text` y no `search`: WebKit le dibuja su propia × a los `search` y
          // quedarían dos, pegadas. No hay regla global que la esconda, y ponerla
          // le sacaría a `Discounts` la única que tiene.
          type="text"
          role="searchbox"
          value={valor}
          onChange={(e) => onCambiar(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onCambiar('')}
          placeholder={`Buscar entre ${cantidad} producto${cantidad === 1 ? '' : 's'}…`}
          aria-label="Buscar productos por nombre, SKU o categoría"
          className="w-full bg-transparent py-5 pl-11 pr-10 text-sm text-brand-black outline-none placeholder:text-neutral-400"
        />
        {valor !== '' && (
          <button
            type="button"
            onClick={() => onCambiar('')}
            aria-label="Borrar la búsqueda"
            className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-brand-black"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onAgregar}
        title="Sumar fotos que ya creaste en Atlas, cada una con su producto"
        className={boton('primario', { chico: true, extra: 'shrink-0 py-2 text-sm' })}
      >
        <ImagePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Agregar productos</span>
        <span className="sm:hidden">Agregar</span>
      </button>
    </div>
  );
}

function EncabezadoDeTabla({
  modo,
  todos,
  algunos,
  onTodos,
}: {
  modo: ModoDeTabla;
  todos: boolean;
  /** Alguno pero no todos: la casilla va indeterminada, no vacía. */
  algunos: boolean;
  onTodos: (v: boolean) => void;
}) {
  return (
    <div
      className="hidden items-center border-b border-neutral-200 bg-neutral-50 py-2.5 px-4 text-[11px] font-medium uppercase tracking-wide text-neutral-500 md:grid"
      style={estiloDeFila(modo)}
    >
      <input
        type="checkbox"
        checked={todos}
        ref={(el) => {
          // Indeterminada solo se puede poner por DOM: no hay atributo de React.
          if (el) el.indeterminate = algunos && !todos;
        }}
        onChange={(e) => onTodos(e.target.checked)}
        aria-label="Seleccionar todos los productos"
        className="h-4 w-4 rounded border-neutral-300 accent-[#1644EB]"
      />
      <span className="truncate">Producto</span>
      {modo === 'carpetas' ? (
        <>
          <span className="truncate">Categoría</span>
          <span className="truncate">Variantes</span>
        </>
      ) : (
        <>
          <span className="truncate">Stock</span>
          <span className="truncate">Precio</span>
          <span className="truncate">SKU</span>
        </>
      )}
      {/* Lo que dice cada pastilla: si está lista, qué le falta o cómo salió. Angosta no
          entra la palabra; queda el punto de color de cada fila. */}
      <span className="truncate">Estado</span>
    </div>
  );
}

/**
 * Una celda de la tabla que, al pasar el mouse, muestra de dónde sale su valor.
 *
 * Con variantes, el stock de la celda es una SUMA y el SKU una lista recortada:
 * los dos son resúmenes de algo que vive repartido, y sin el desglose el usuario
 * tendría que abrir la ficha para saber a qué talle le falta stock. Es
 * informativo y nada más — para editar está Modificar.
 *
 * Solo aparece si hay algo que desglosar. Sin variantes el número de la celda ya
 * ES el dato completo.
 */
function CeldaConDetalle({
  valor,
  clase,
  titulo,
  campo,
  detalle,
}: {
  valor: string;
  clase: string;
  titulo: string;
  campo: 'stock' | 'sku';
  detalle: DetalleDeVariante[];
}) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  if (detalle.length === 0) return <span className={clase}>{valor}</span>;

  return (
    <span
      ref={ref}
      onMouseEnter={() => setAbierto(true)}
      onMouseLeave={() => setAbierto(false)}
      className={`${clase} cursor-default underline decoration-neutral-300 decoration-dotted underline-offset-4`}
    >
      {valor}
      <PopoverAnclado
        abierto={abierto}
        anclaRef={ref}
        onCerrar={() => setAbierto(false)}
        ancho={248}
        sinVelo
        rol="tooltip"
      >
        <p className="border-b border-neutral-100 py-2 px-3 text-[11px] font-medium text-neutral-500">
          {titulo}
        </p>
        <ul className="py-1">
          {detalle.map((d, i) => (
            <li key={i} className="flex items-baseline justify-between gap-3 py-1.5 px-3">
              <span className="min-w-0 truncate text-xs text-neutral-500">{d.etiqueta}</span>
              <span
                className={`shrink-0 text-xs ${
                  d[campo] === SIN_DATO ? 'text-neutral-300' : 'text-brand-black'
                }`}
              >
                {d[campo]}
              </span>
            </li>
          ))}
        </ul>
      </PopoverAnclado>
    </span>
  );
}

function Miniatura({ imagenes }: { imagenes: ProductoDeLanzamiento['imagenes'] }) {
  return (
    <span className="relative w-12 h-12 shrink-0 overflow-hidden rounded-xl bg-neutral-100">
      {imagenes[0]?.thumbUrl && (
        <CachedImg src={imagenes[0].thumbUrl} alt="" className="w-full h-full object-cover" />
      )}
    </span>
  );
}

/** Qué es este producto para el lanzamiento: sus fotos, sus variantes y si en la
 *  tienda se va a crear o ya existe. */
function Subtitulo({
  producto: p,
  modo,
  cambiosEnLaTienda = null,
}: {
  producto: ProductoDeLanzamiento;
  modo: ModoDeTabla;
  cambiosEnLaTienda?: number | null;
}) {
  const fotos = p.imagenes.length;
  const variantes = p.variantes.length;
  const yaEsta = p.tnProductId != null;
  return (
    <span className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-neutral-500">
      <span className="truncate">
        {fotos} foto{fotos === 1 ? '' : 's'}
        {variantes > 0 && ` · ${variantes} variante${variantes === 1 ? '' : 's'}`}
      </span>
      {/* "Ya en la tienda" es de Tiendanube. Exportando carpetas no hay tienda
          donde estar, y decirlo sería hablar de algo que no existe. Es una etiqueta
          y no texto corrido: "se va a crear" al final del renglón se leía como un
          estado más, y es otra cosa — si en la tienda se crea o se actualiza. */}
      {modo === 'tienda' && (
        <span
          title={
            yaEsta
              ? 'Ya existe en tu tienda: al publicar se actualiza lo que cambiaste y se suman las fotos'
              : 'Todavía no existe en tu tienda: se crea al publicar'
          }
          className={`hidden shrink-0 rounded-md py-px px-1.5 text-[10px] font-medium sm:inline ${
            yaEsta && !cambiosEnLaTienda ? 'bg-neutral-100 text-neutral-600' : 'bg-[#1644EB]/[0.07] text-[#1644EB]'
          }`}
        >
          {yaEsta
            ? cambiosEnLaTienda
              ? `Ya en tu tienda · ${cambiosEnLaTienda} cambio${cambiosEnLaTienda === 1 ? '' : 's'}`
              : 'Ya en tu tienda'
            : 'Producto nuevo'}
        </span>
      )}
      {/* Sólo Mercado Libre (traído de principal, f25f4fe7): la misma etiqueta, cuando ya se publicó. */}
      {modo === 'mercadolibre' && yaPublicadoEnMl(p) && (
        <span className="hidden shrink-0 rounded-md bg-neutral-100 py-px px-1.5 text-[10px] font-medium text-neutral-600 sm:inline">
          Ya en Mercado Libre
        </span>
      )}
    </span>
  );
}

/**
 * Una fila de la tabla. Toda la fila abre la ficha; el botón "Modificar" es la
 * parte visible de eso y por eso es un `span` y no un `button` — un botón dentro
 * de otro no es HTML válido y el lector de pantalla anunciaría dos acciones para
 * una sola.
 */
function FilaDeProducto({
  producto: p,
  estado,
  modo,
  categorias,
  cambiosEnLaTienda,
  elegido,
  onAbrir,
  onElegir,
}: {
  producto: ProductoDeLanzamiento;
  /** Lo que dice la pastilla. Lo calcula la tabla: depende de las tiendas del lanzamiento. */
  estado: EstadoDeFila;
  modo: ModoDeTabla;
  /** El árbol, para poder mostrar el camino de la categoría en modo carpetas. */
  categorias: CategoriaDeTienda[];
  /** Ya está en la tienda: cuántos campos difieren de lo publicado. null = no se sabe o no aplica. */
  cambiosEnLaTienda: number | null;
  /** Tildado para las acciones en lote (y lo que se publica). */
  elegido: boolean;
  onAbrir: () => void;
  onElegir: () => void;
}) {
  const r = resumenDeFila(p);
  const c = resumenDeCarpetas(p, categorias);
  /** Lo que falta se muestra apagado: la tabla está para ver los huecos. */
  const tono = (v: string) => (v === SIN_DATO ? 'text-neutral-300' : 'text-brand-black');
  /**
   * El hueco que FRENA la publicación no es un guion más: la celda dice "Falta" en
   * ámbar, la misma voz que la pastilla de la fila
   * (ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta).
   */
  const falta = (columna: 'precio' | 'sku' | 'stock', v: string) =>
    estado.columna === columna && v === SIN_DATO ? 'Falta' : v;
  const tonoDe = (columna: 'precio' | 'sku' | 'stock', v: string) =>
    estado.columna === columna && v === SIN_DATO ? 'font-medium text-amber-700' : tono(v);
  const celda = 'hidden overflow-hidden whitespace-nowrap text-sm md:block';
  return (
    <div
      className="group relative flex w-full items-center gap-4 py-3 px-4 text-left transition-colors hover:bg-neutral-50 md:grid"
      style={estiloDeFila(modo)}
    >
      <input
        type="checkbox"
        checked={elegido}
        onChange={() => onElegir()}
        aria-label={`Seleccionar ${p.nombre}`}
        className="h-4 w-4 shrink-0 rounded border-neutral-300 accent-[#1644EB]"
      />

      <button
        type="button"
        onClick={onAbrir}
        aria-label={`Modificar ${p.nombre}`}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <Miniatura imagenes={p.imagenes} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-brand-black">{p.nombre}</span>
          <Subtitulo producto={p} modo={modo} cambiosEnLaTienda={cambiosEnLaTienda} />
          {/* En el teléfono no entran cinco columnas: lo mismo, en una línea, y el
              estado con su color abajo. */}
          {/* Solo lo que está cargado: "— · Stock —" era un renglón de guiones, y lo que
              falta ya lo dice la pastilla de abajo. */}
          {(() => {
            const linea =
              modo === 'carpetas'
                ? [c.categoria]
                : [r.precio, r.stock === SIN_DATO ? SIN_DATO : `Stock ${r.stock}`];
            const cargado = linea.filter((v) => v !== SIN_DATO);
            return cargado.length > 0 ? (
              <span className="block truncate text-xs text-neutral-500 md:hidden">{cargado.join(' · ')}</span>
            ) : null;
          })()}
          <span className="mt-1 block md:hidden">
            <PastillaDeEstado estado={estado} />
          </span>
        </span>
      </button>

      {modo === 'carpetas' ? (
        <>
          {/* `title` y no un popover: es texto que a veces no entra, no un
              resumen de algo repartido. */}
          <span className={`${celda} ${tono(c.categoria)}`} title={c.categoria}>
            {c.categoria}
          </span>
          <span className={`${celda} ${tono(c.variantes)}`} title={c.variantes}>
            {c.variantes}
          </span>
        </>
      ) : (
        <>
          <CeldaConDetalle
            valor={falta('stock', r.stock)}
            clase={`${celda} ${tonoDe('stock', r.stock)}`}
            titulo="Stock por variante"
            campo="stock"
            detalle={r.porVariante}
          />
          <span className={`${celda} ${tonoDe('precio', r.precio)}`}>{falta('precio', r.precio)}</span>
          <CeldaConDetalle
            valor={falta('sku', r.sku)}
            clase={`${celda} ${tonoDe('sku', r.sku)}`}
            titulo="SKU por variante"
            campo="sku"
            detalle={r.porVariante}
          />
        </>
      )}
      <span className="hidden min-w-0 items-center justify-between gap-2 md:flex">
        <span className="flex min-w-0 items-center gap-2">
          <PastillaDeEstado estado={estado} />
          <EstadoPorTienda producto={p} />
        </span>
        {/* Botón de verdad y ya no un `span`: la fila dejó de ser un botón para
            que la casilla se pueda tildar sin abrir la ficha, así que el chip
            necesita su propio click. */}
        <button
          type="button"
          onClick={onAbrir}
          aria-label={`Modificar ${p.nombre}`}
          data-tour="tienda-modificar"
          className={boton('secundario', { chico: true })}
        >
          Modificar
        </button>
      </span>

      <button
        type="button"
        onClick={onAbrir}
        aria-label={`Modificar ${p.nombre}`}
        className="ml-auto flex shrink-0 items-center gap-2 md:hidden"
      >
        <EstadoPorTienda producto={p} />
        <ChevronRight className="w-4 h-4 text-neutral-300" />
      </button>
    </div>
  );
}

function IconoDeEstado({ estado, chico }: { estado: string; chico?: boolean }) {
  const t = chico ? 'w-3.5 h-3.5' : 'w-4 h-4';
  if (estado === 'publicado') return <Check className={`${t} shrink-0 text-green-600`} />;
  if (estado === 'publicando') return <Loader2 className={`${t} shrink-0 animate-spin text-blue-600`} />;
  if (estado === 'error' || estado === 'con_errores') return <AlertCircle className={`${t} shrink-0 text-red-600`} />;
  return null;
}

/** El color de cada estado de la pastilla. */
const COLOR_DE_ESTADO: Record<TonoDeEstado, { pastilla: string; punto: string }> = {
  listo: { pastilla: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15', punto: 'bg-emerald-500' },
  falta: { pastilla: 'bg-amber-50 text-amber-800 ring-amber-600/20', punto: 'bg-amber-400' },
  publicado: { pastilla: 'bg-[#1644EB]/[0.07] text-[#1644EB] ring-[#1644EB]/15', punto: 'bg-[#1644EB]' },
  publicando: { pastilla: 'bg-sky-50 text-sky-700 ring-sky-600/15', punto: 'bg-sky-500' },
  error: { pastilla: 'bg-red-50 text-red-700 ring-red-600/15', punto: 'bg-red-500' },
};

/**
 * La pastilla de la columna Estado (ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta):
 * qué le falta al producto o cómo salió, con la explicación entera al pasar el mouse.
 */
function PastillaDeEstado({ estado }: { estado: EstadoDeFila }) {
  const c = COLOR_DE_ESTADO[estado.tono];
  return (
    <span
      title={estado.detalle ?? undefined}
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full py-1 px-2.5 text-[11px] font-medium ring-1 ring-inset ${c.pastilla}`}
    >
      {estado.tono === 'publicando' ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
      ) : estado.tono === 'publicado' ? (
        <Check className="h-3 w-3 shrink-0" strokeWidth={2.5} />
      ) : (
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.punto}`} aria-hidden="true" />
      )}
      <span className="truncate">{estado.texto}</span>
    </span>
  );
}

const TEXTO_DE_ESTADO: Record<string, string> = {
  publicado: 'publicado',
  publicando: 'publicando',
  error: 'no se pudo publicar',
  con_errores: 'publicado con errores',
  pendiente: 'sin publicar',
};

/**
 * Con varias tiendas, cómo salió en cada una, con su logo: "✓ Tiendanube · ✕ Mercado
 * Libre", con el motivo al pasar el mouse (ADR-2026-09-30-la-ficha-de-mercado-libre).
 * Con una sola tienda la pastilla ya lo dice todo y esto no dibuja nada.
 */
function EstadoPorTienda({ producto }: { producto: ProductoDeLanzamiento }) {
  const porDestino = Object.entries(producto.porDestino ?? {});
  // Antes de publicar no hay nada que contar por tienda: los logos solos repetirían el panel.
  if (porDestino.length <= 1 || porDestino.every(([, e]) => e?.estado === 'pendiente')) return null;
  return (
    <span className="flex shrink-0 items-center gap-2">
      {porDestino.map(([d, e]) => (
        <span
          key={d}
          className="inline-flex items-center gap-0.5"
          title={`${nombreCanal(d as CanalLanzamiento)}: ${TEXTO_DE_ESTADO[e!.estado] ?? e!.estado}${e!.error ? ` — ${e!.error}` : ''}`}
        >
          {d === 'mercadolibre' ? (
            <MercadoLibreIcon className="h-3.5 w-3.5" />
          ) : (
            <TiendaNubeIconCompacto className="h-3.5 w-3.5 shrink-0 text-[#2c3357]" />
          )}
          <IconoDeEstado estado={e!.estado} chico />
        </span>
      ))}
    </span>
  );
}

/**
 * El lanzamiento sin productos. Era una caja punteada gris con un ícono de "sin
 * imagen": se leía como algo roto, no como el primer paso. Ahora muestra un mosaico
 * de fotos, la acción principal y las dos formas de sumar productos
 * (ADR-2026-10-05-los-botones-de-la-tienda-son-de-atlas). La acción sigue ACÁ y no en
 * otra pantalla (ADR-2026-09-17-la-ficha-vacia-del-lanzamiento-elige-creaciones).
 */
function EstadoVacio({ onElegir, onIrACreaciones }: { onElegir: () => void; onIrACreaciones: () => void }) {
  return (
    <div className="mt-6 overflow-hidden rounded-3xl border border-[#1644EB]/10 bg-gradient-to-br from-[#F3F6FF] via-white to-[#EFFCFD]">
      <div className="grid items-center gap-8 p-6 md:grid-cols-[1fr_1.1fr] md:p-10">
        <div className="text-center md:text-left">
          <MosaicoVacio />
          <p className="mt-6 text-lg font-semibold text-brand-black">Sumá los primeros productos</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-neutral-500 md:mx-0">
            Elegí fotos que ya creaste en Atlas. Cada una entra con su producto; si no tiene, lo elegís
            vos.
          </p>
          <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row md:justify-start">
            <button type="button" onClick={onElegir} data-tour="tienda-elegir" className={boton('primario')}>
              <ImagePlus className="h-4 w-4" />
              Elegir creaciones
            </button>
            <button type="button" onClick={onIrACreaciones} className={boton('secundario')}>
              Ir a Creaciones
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <ol className="space-y-3">
          {[
            {
              titulo: 'Desde acá',
              texto: 'Tocá “Elegir creaciones” y marcá todas las fotos que quieras sumar de una vez.',
            },
            {
              titulo: 'Desde Creaciones',
              texto: 'En cualquier foto, tocá “Publicar en tu tienda online” y elegí este lanzamiento.',
            },
            {
              titulo: 'Con el agente',
              texto: 'Pedile a Atlas que sume las fotos por vos, desde el botón “Preguntále a Atlas”.',
            },
          ].map((f, i) => (
            <li key={f.titulo} className="flex gap-3 rounded-2xl border border-white bg-white/80 p-4 shadow-sm">
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#EEF2FF] text-xs font-semibold text-[#1644EB]">
                {i + 1}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-brand-black">{f.titulo}</span>
                <span className="mt-0.5 block text-sm leading-snug text-neutral-500">{f.texto}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/** Tres "fotos" en abanico: dice "acá van imágenes" sin depender de que haya alguna. */
function MosaicoVacio() {
  return (
    <div className="relative mx-auto h-24 w-36 md:mx-0" aria-hidden="true">
      <span className="absolute left-2 top-3 h-20 w-16 -rotate-12 rounded-xl bg-gradient-to-br from-[#C7D4FF] to-[#E8EEFF] shadow-sm" />
      <span className="absolute right-2 top-3 h-20 w-16 rotate-12 rounded-xl bg-gradient-to-br from-[#B8F1F5] to-[#E3FAFC] shadow-sm" />
      <span className="absolute left-1/2 top-0 grid h-24 w-[4.5rem] -translate-x-1/2 place-items-center rounded-xl bg-white text-[#1644EB] shadow-md ring-1 ring-[#1644EB]/10">
        <Images className="h-7 w-7" />
      </span>
    </div>
  );
}
